import { create } from "zustand";
import {
  BuildJobListInfo,
  BuildJobListInfoSchema,
  BuildJobStatusSchema,
  BuildType,
  BuildTypeSchema,
  PaginationInfo,
} from "../../darvin-shared/schema/zod/build";
import { BuildApi } from "../api/buildApi";
import { ChatApi } from "../api/chatApi";
import { logProjectEvent } from "../utils/logUtils";
import { useProjectStore } from "./useProjectStore";

export enum BuildPlatform {
  WEB = "web",
  DESKTOP = "desktop",
  ANDROID = "android",
  IOS = "ios",
}

class PlatformBuilds {
  constructor(
    public builds: BuildJobListInfo[] | null,
    public pagination: PaginationInfo | null
  ) {}
}

export class WebBuilds extends PlatformBuilds {
  constructor(
    public previewUrl: string | null,
    public builds: BuildJobListInfo[] | null,
    public pagination: PaginationInfo | null
  ) {
    super(builds, pagination);
  }
}

export class AndroidBuilds extends PlatformBuilds {
  constructor(
    public builds: BuildJobListInfo[] | null,
    public pagination: PaginationInfo | null
  ) {
    super(builds, pagination);
  }
}

interface ProjectBuildsActions {
  loadBuilds: (
    buildPlatform: BuildPlatform,
    nextPage: boolean
  ) => Promise<void>;
  startWebBuild: () => Promise<void>;
  startAndroidBuild: (buildType: "apk" | "aab") => Promise<void>;
  loadAndroidBuildUrl: (buildId: string) => Promise<string | null>;
  processingBuildsChanged: (processingBuilds: BuildJobListInfo[]) => void;
  loadErrorMessage: (buildId: string) => Promise<string | null>;
  resetCompletedBuild: () => void;
  resetErrorBuild: () => void;
  dispose: () => void;
}

interface ProjectBuildsState {
  web: WebBuilds | null;
  android: AndroidBuilds | null;

  // Only to show snackbar
  completedBuild: BuildType | null;
  errorBuild: BuildType | null;

  actions: ProjectBuildsActions;
}

export const useProjectBuildsStore = create<ProjectBuildsState>((set, get) => {
  let lastProcessingBuilds: BuildJobListInfo[] | null = null;

  function getDataForPlatform(buildPlatform: BuildPlatform) {
    if (buildPlatform === BuildPlatform.WEB) {
      return get().web;
    } else if (buildPlatform === BuildPlatform.ANDROID) {
      return get().android;
    }
    return null;
  }

  function getBuildTypesForPlatform(buildPlatform: BuildPlatform) {
    if (buildPlatform === BuildPlatform.WEB) {
      return [BuildTypeSchema.Enum.web];
    } else if (buildPlatform === BuildPlatform.ANDROID) {
      return [BuildTypeSchema.Enum.apk, BuildTypeSchema.Enum.aab];
    }
    return [];
  }

  async function handleUpdatedWebBuild(build: BuildJobListInfo) {
    const projectId = useProjectStore.getState().projectId;
    if (projectId) {
      logProjectEvent(projectId, "builds-store-update-web-build", {
        buildId: build.buildId,
        buildType: build.buildType,
        status: build.status,
        previousBuildsCount: get().web?.builds?.length ?? 0,
      });
    }

    const updatedWebBuilds = [...(get().web?.builds ?? [])];
    const buildIndex = updatedWebBuilds.findIndex(
      (el) => el.buildId === build.buildId
    );
    if (buildIndex !== -1) {
      updatedWebBuilds[buildIndex] = BuildJobListInfoSchema.parse(build);
    }

    let newPreviewUrl = null;
    if (build.status === BuildJobStatusSchema.Enum.complete) {
      newPreviewUrl = await BuildApi.loadUrl(
        useProjectStore.getState().projectId!,
        build.buildId
      );
      if (projectId) {
        logProjectEvent(projectId, "builds-store-web-preview-url-loaded", {
          buildId: build.buildId,
          hasUrl: !!newPreviewUrl,
        });
      }
    }

    let finishedBuildInfo = {};
    if (build.status === BuildJobStatusSchema.Enum.complete) {
      finishedBuildInfo = {
        completedBuild: BuildTypeSchema.Enum.web,
      };
    } else if (
      build.status === BuildJobStatusSchema.Enum.error ||
      build.status === BuildJobStatusSchema.Enum.cancelled
    ) {
      finishedBuildInfo = {
        errorBuild: BuildTypeSchema.Enum.web,
      };
    }

    set({
      web: new WebBuilds(
        newPreviewUrl ?? get().web?.previewUrl ?? null,
        updatedWebBuilds,
        get().web?.pagination ?? null
      ),
      ...finishedBuildInfo,
    });

    if (projectId && Object.keys(finishedBuildInfo).length > 0) {
      logProjectEvent(projectId, "builds-store-web-build-finished", {
        buildId: build.buildId,
        finalStatus: build.status,
        finishedBuildInfo,
      });
    }
  }

  async function handleUpdatedAndroidBuild(build: BuildJobListInfo) {
    const projectId = useProjectStore.getState().projectId;
    if (projectId) {
      logProjectEvent(projectId, "builds-store-update-android-build", {
        buildId: build.buildId,
        buildType: build.buildType,
        status: build.status,
        previousBuildsCount: get().android?.builds?.length ?? 0,
      });
    }

    const updatedAndroidBuilds = [...(get().android?.builds ?? [])];
    const buildIndex = updatedAndroidBuilds.findIndex(
      (el) => el.buildId === build.buildId
    );
    if (buildIndex !== -1) {
      updatedAndroidBuilds[buildIndex] = BuildJobListInfoSchema.parse(build);
    }

    let finishedBuildInfo = {};
    if (build.status === BuildJobStatusSchema.Enum.complete) {
      finishedBuildInfo = {
        completedBuild: build.buildType,
      };
    } else if (
      build.status === BuildJobStatusSchema.Enum.error ||
      build.status === BuildJobStatusSchema.Enum.cancelled
    ) {
      finishedBuildInfo = {
        errorBuild: build.buildType,
      };
    }

    set({
      android: new AndroidBuilds(
        updatedAndroidBuilds,
        get().android?.pagination ?? null
      ),
      ...finishedBuildInfo,
    });

    if (projectId && Object.keys(finishedBuildInfo).length > 0) {
      logProjectEvent(projectId, "builds-store-android-build-finished", {
        buildId: build.buildId,
        buildType: build.buildType,
        finalStatus: build.status,
        finishedBuildInfo,
      });
    }
  }

  return {
    web: null,
    android: null,
    completedBuild: null,
    errorBuild: null,
    idsOfProcessingBuilds: null,

    actions: {
      loadBuilds: async (buildPlatform: BuildPlatform, nextPage: boolean) => {
        const projectId = useProjectStore.getState().projectId;
        const existingData = getDataForPlatform(buildPlatform);

        if (projectId) {
          const jobsResponse = await BuildApi.loadBuilds({
            projectId,
            ...(nextPage && {
              startAfterBuildId: existingData?.pagination?.nextCursor,
            }),
            buildTypes: getBuildTypesForPlatform(buildPlatform),
          });

          let previewUrl = null;
          if (buildPlatform === BuildPlatform.WEB) {
            const latestCompleteBuild = [...jobsResponse.builds]
              .sort((a, b) => {
                const dateA = new Date(a.lastUpdateTimestamp);
                const dateB = new Date(b.lastUpdateTimestamp);
                return dateB.valueOf() - dateA.valueOf();
              })
              .find((el) => el.status === BuildJobStatusSchema.Enum.complete);
            if (latestCompleteBuild) {
              previewUrl = await BuildApi.loadUrl(
                projectId,
                latestCompleteBuild.buildId
              );
            }
          }

          if (buildPlatform === BuildPlatform.WEB) {
            set({
              web: new WebBuilds(
                previewUrl,
                nextPage
                  ? [...(existingData?.builds ?? []), ...jobsResponse.builds]
                  : jobsResponse.builds,
                jobsResponse.pagination
              ),
            });
          } else if (buildPlatform === BuildPlatform.ANDROID) {
            set({
              android: new AndroidBuilds(
                nextPage
                  ? [...(existingData?.builds ?? []), ...jobsResponse.builds]
                  : jobsResponse.builds,
                jobsResponse.pagination
              ),
            });
          }
        }
      },

      startWebBuild: async () => {
        const projectId = useProjectStore.getState().projectId;

        if (!projectId) {
          return;
        }

        const checkpointInfo = await ChatApi.loadCheckpointInfo(projectId);
        const buildResponse = await BuildApi.startBuild(
          projectId,
          BuildTypeSchema.Enum.web,
          checkpointInfo.data.currentCodeRevision
        );
        const buildDetails = await BuildApi.loadSingleBuild(
          projectId,
          buildResponse.buildId
        );
        set((state) => ({
          web: new WebBuilds(
            state?.web?.previewUrl ?? null,
            [
              BuildJobListInfoSchema.parse(buildDetails.buildJob),
              ...(state?.web?.builds ?? []),
            ],
            state?.web?.pagination ?? null
          ),
        }));
      },

      startAndroidBuild: async (buildType: "apk" | "aab") => {
        const projectId = useProjectStore.getState().projectId;

        if (!projectId) {
          return;
        }

        let apkBuildResponse = null;
        let aabBuildResponse = null;
        let apkBuildDetails = null;
        let aabBuildDetails = null;
        const checkpointInfo = await ChatApi.loadCheckpointInfo(projectId);
        if (buildType === "apk") {
          apkBuildResponse = await BuildApi.startBuild(
            projectId,
            BuildTypeSchema.Enum.apk,
            checkpointInfo.data.currentCodeRevision
          );
          apkBuildDetails = await BuildApi.loadSingleBuild(
            projectId,
            apkBuildResponse.buildId
          );
        } else if (buildType === "aab") {
          aabBuildResponse = await BuildApi.startBuild(
            projectId,
            BuildTypeSchema.Enum.aab,
            checkpointInfo.data.currentCodeRevision
          );
          aabBuildDetails = await BuildApi.loadSingleBuild(
            projectId,
            aabBuildResponse.buildId
          );
        }

        set((state) => ({
          android: new AndroidBuilds(
            [
              ...(apkBuildDetails
                ? [BuildJobListInfoSchema.parse(apkBuildDetails.buildJob)]
                : []),
              ...(aabBuildDetails
                ? [BuildJobListInfoSchema.parse(aabBuildDetails.buildJob)]
                : []),
              ...(state?.android?.builds ?? []),
            ],
            state?.android?.pagination ?? null
          ),
        }));
      },

      loadAndroidBuildUrl: async (buildId: string): Promise<string | null> => {
        const projectId = useProjectStore.getState().projectId;
        return projectId ? await BuildApi.loadUrl(projectId, buildId) : null;
      },

      processingBuildsChanged: async (
        processingBuilds: BuildJobListInfo[]
      ): Promise<void> => {
        const projectId = useProjectStore.getState().projectId;

        if (projectId) {
          logProjectEvent(projectId, "builds-store-processing-builds-changed", {
            processingBuildsCount: processingBuilds.length,
            processingBuildIds: processingBuilds.map((b) => b.buildId),
            processingBuildTypes: processingBuilds.map((b) => b.buildType),
            processingBuildStatuses: processingBuilds.map((b) => b.status),
          });
        }

        if (
          JSON.stringify(processingBuilds) ===
          JSON.stringify(lastProcessingBuilds)
        ) {
          if (projectId) {
            logProjectEvent(projectId, "builds-store-no-changes-detected", {
              note: "Processing builds unchanged, skipping update",
            });
          }
          return;
        }
        lastProcessingBuilds = processingBuilds;
        set({
          completedBuild: null,
          errorBuild: null,
        });

        const currentAllBuilds = [
          ...(get().web?.builds ?? []),
          ...(get().android?.builds ?? []),
        ];
        const currentAllProcessingBuilds = currentAllBuilds.filter(
          (build) =>
            build.status === BuildJobStatusSchema.Enum.waiting ||
            build.status === BuildJobStatusSchema.Enum.running
        );

        if (projectId) {
          logProjectEvent(projectId, "builds-store-current-state", {
            currentAllBuildsCount: currentAllBuilds.length,
            currentProcessingBuildsCount: currentAllProcessingBuilds.length,
            currentProcessingBuildIds: currentAllProcessingBuilds.map(
              (b) => b.buildId
            ),
            webBuildsCount: get().web?.builds?.length ?? 0,
            androidBuildsCount: get().android?.builds?.length ?? 0,
          });
        }

        if (currentAllProcessingBuilds.length === 0) {
          if (projectId) {
            logProjectEvent(projectId, "builds-store-initial-state", {
              note: "No current processing builds, initializing store with new builds",
              newProcessingBuildsCount: processingBuilds.length,
            });
          }

          const webBuilds = [];
          const androidBuilds = [];
          for (const processingBuild of processingBuilds) {
            if (processingBuild.buildType === BuildTypeSchema.Enum.web) {
              webBuilds.push(processingBuild);
            } else if (
              processingBuild.buildType === BuildTypeSchema.Enum.apk ||
              processingBuild.buildType === BuildTypeSchema.Enum.aab
            ) {
              androidBuilds.push(processingBuild);
            }
          }
          set({
            web:
              webBuilds.length > 0
                ? new WebBuilds(
                    get().web?.previewUrl ?? null,
                    webBuilds,
                    get().web?.pagination ?? null
                  )
                : null,
            android:
              androidBuilds.length > 0
                ? new AndroidBuilds(
                    androidBuilds,
                    get().android?.pagination ?? null
                  )
                : null,
          });

          if (projectId) {
            logProjectEvent(projectId, "builds-store-initial-state-set", {
              webBuildsSet: webBuilds.length,
              androidBuildsSet: androidBuilds.length,
            });
          }
          return;
        }

        for (const build of currentAllProcessingBuilds) {
          const matchingProcessingBuild = processingBuilds.find(
            (el) => el.buildId === build.buildId
          );
          if (
            matchingProcessingBuild &&
            matchingProcessingBuild.status !== build.status
          ) {
            if (projectId) {
              logProjectEvent(projectId, "builds-store-status-changed", {
                buildId: build.buildId,
                buildType: build.buildType,
                oldStatus: build.status,
                newStatus: matchingProcessingBuild.status,
              });
            }

            if (build.buildType === BuildTypeSchema.Enum.web) {
              handleUpdatedWebBuild(matchingProcessingBuild);
            } else if (
              build.buildType === BuildTypeSchema.Enum.apk ||
              build.buildType === BuildTypeSchema.Enum.aab
            ) {
              handleUpdatedAndroidBuild(matchingProcessingBuild);
            }
          } else if (!matchingProcessingBuild) {
            if (projectId) {
              logProjectEvent(
                projectId,
                "builds-store-build-not-in-processing",
                {
                  buildId: build.buildId,
                  buildType: build.buildType,
                  currentStatus: build.status,
                  note: "Build finished, fetching final status",
                }
              );
            }

            const updatedBuild = await BuildApi.loadSingleBuild(
              projectId!,
              build.buildId
            );
            const buildInfo = BuildJobListInfoSchema.parse(
              updatedBuild.buildJob
            );

            if (projectId) {
              logProjectEvent(projectId, "builds-store-final-status-fetched", {
                buildId: build.buildId,
                buildType: build.buildType,
                finalStatus: buildInfo.status,
              });
            }

            if (build.buildType === BuildTypeSchema.Enum.web) {
              handleUpdatedWebBuild(buildInfo);
            } else if (
              build.buildType === BuildTypeSchema.Enum.apk ||
              build.buildType === BuildTypeSchema.Enum.aab
            ) {
              handleUpdatedAndroidBuild(buildInfo);
            }
          }
        }
      },

      loadErrorMessage: async (buildId: string): Promise<string | null> => {
        const projectId = useProjectStore.getState().projectId;
        if (projectId) {
          const buildResponse = await BuildApi.loadSingleBuild(
            projectId,
            buildId
          );
          return buildResponse.buildJob.errorMessage ?? null;
        }
        return null;
      },

      resetCompletedBuild: () => {
        set({
          completedBuild: null,
        });
      },
      resetErrorBuild: () => {
        set({
          errorBuild: null,
        });
      },

      dispose: () => {
        set({
          web: null,
          android: null,
          completedBuild: null,
          errorBuild: null,
        });
      },
    },
  };
});

export const useProjectBuildsWeb = () =>
  useProjectBuildsStore((state) => state.web);

export const useProjectBuildsAndroid = () =>
  useProjectBuildsStore((state) => state.android);

export const useProjectBuildCompletedBuild = () =>
  useProjectBuildsStore((state) => state.completedBuild);

export const useProjectBuildErrorBuild = () =>
  useProjectBuildsStore((state) => state.errorBuild);

export const useProjectBuildsProcessingBuildIds = () =>
  useProjectBuildsStore((state) => {
    const allBuilds = [
      ...(state.web?.builds ?? []),
      ...(state.android?.builds ?? []),
    ];
    return allBuilds
      .filter(
        (build) =>
          build.status === BuildJobStatusSchema.Enum.waiting ||
          build.status === BuildJobStatusSchema.Enum.running
      )
      .map((build) => build.buildId);
  });

export const useProjectBuildActions = () =>
  useProjectBuildsStore((state) => state.actions);
